import React, { memo } from 'react';
import { Box, Typography, Grid, Alert, Button } from '@mui/material';
import { useSchoolAdminDashboard } from '../../hooks/useDashboard';
import { useSubscription } from '../../context/SubscriptionContext';
import {
    EmployeeSummaryCard,
    EmployeeSummaryCardSkeleton,
    AbsentEmployeesCard,
    AbsentEmployeesCardSkeleton,
    PendingLeavesCard,
    PendingLeavesCardSkeleton,
    QuickInfoCards,
    QuickInfoCardsSkeleton,
    DashboardRefreshButton,
} from '../../components/dashboard';

/**
 * School Admin Dashboard
 * Elegant, professional dashboard for School Administrators
 * Clean layout with refined aesthetics and dark theme support
 */
const SchoolAdminDashboard: React.FC = memo(() => {
    const { data, loading, error, refetch } = useSchoolAdminDashboard();
    const { hasFeature } = useSubscription();

    // Feature flags for conditional card rendering
    const showAttendanceCards = hasFeature('attendance');
    const showLeavesCards = hasFeature('leaves');
    const showExaminationCards = hasFeature('examination');

    // Calculate how many top row cards are visible for dynamic grid sizing
    const topRowCardCount = 1 + (showAttendanceCards ? 1 : 0) + (showLeavesCards ? 1 : 0);
    
    // Dynamic grid sizing: cards expand to fill available space
    const getGridSize = (index: number) => {
        if (topRowCardCount === 1) return { xs: 12 }; // Single card takes full width
        if (topRowCardCount === 2) return { xs: 12, sm: 6 }; // Two cards: 50% each
        return { xs: 12, sm: 6, lg: 4 }; // Three cards: 33% each on lg
    };

    // Error state
    if (error) {
        return (
            <Box>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={refetch}>
                            Retry
                        </Button>
                    }
                    sx={{ borderRadius: 2 }}
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    // Loading state with elegant skeleton
    if (loading || !data) {
        return (
            <Box>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography 
                        variant="h4" 
                        fontWeight={600} 
                        color="text.primary"
                        gutterBottom
                    >
                        Dashboard
                    </Typography>
                </Box>

                {/* Row 1: Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={getGridSize(0)}>
                        <EmployeeSummaryCardSkeleton />
                    </Grid>
                    {showAttendanceCards && (
                        <Grid size={getGridSize(1)}>
                            <AbsentEmployeesCardSkeleton />
                        </Grid>
                    )}
                    {showLeavesCards && (
                        <Grid size={getGridSize(2)}>
                            <PendingLeavesCardSkeleton />
                        </Grid>
                    )}
                </Grid>

                {/* Row 2: Quick Info (Exams, Holidays & Events) */}
                <QuickInfoCardsSkeleton 
                    showExams={showExaminationCards} 
                    showHolidays={showAttendanceCards}
                    showEvents={true}
                />
            </Box>
        );
    }

    const {
        stats,
        employeesByRole,
        absentToday,
        pendingLeaves,
        exams,
        upcomingHolidays,
        upcomingEvents
    } = data;

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography 
                        variant="h4" 
                        fontWeight={600} 
                        color="text.primary"
                        gutterBottom
                    >
                        Dashboard
                    </Typography>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                    >
                        Welcome back! Here's what's happening at your school.
                    </Typography>
                </Box>
                <DashboardRefreshButton onClick={refetch} />
            </Box>

            {/* Row 1: Summary Cards - dynamic sizing based on visible cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={getGridSize(0)}>
                    <EmployeeSummaryCard
                        total={stats.totalEmployees}
                        studentCount={stats.totalStudents}
                        byRole={employeesByRole}
                    />
                </Grid>
                {showAttendanceCards && (
                    <Grid size={getGridSize(1)}>
                        <AbsentEmployeesCard data={absentToday} />
                    </Grid>
                )}
                {showLeavesCards && (
                    <Grid size={getGridSize(2)}>
                        <PendingLeavesCard data={pendingLeaves} />
                    </Grid>
                )}
            </Grid>

            {/* Row 2: Quick Info (Exams, Holidays & Events) */}
            <Box sx={{ mb: 2 }}>
                <QuickInfoCards 
                    exams={exams} 
                    holidays={upcomingHolidays}
                    events={upcomingEvents}
                    showExams={showExaminationCards}
                    showHolidays={showAttendanceCards}
                    showEvents={true}
                />
            </Box>


            
        </Box>
    );
});

SchoolAdminDashboard.displayName = 'SchoolAdminDashboard';

export default SchoolAdminDashboard;
